<template>
  <div class="role-board" :style="{ '--role': info.colour }">
    <header v-if="!compact" class="role-board__head">
      <h3 class="role-board__title">{{ info.label }}</h3>
      <span class="role-board__tagline">{{ info.tagline }}</span>
    </header>

    <FrontierBoard v-if="role === 'military'" :game="game" :readonly="readonly" :preview="preview" :hold="hold" @act="(p) => $emit('act', p)" />
    <StewardsBoard v-else-if="role === 'trade'" :game="game" :readonly="readonly" :preview="preview" @act="(p) => $emit('act', p)" @undo="$emit('undo')" />
    <HallBoard v-else-if="role === 'culture'" :game="game" :readonly="readonly" :preview="preview" @act="(p) => $emit('act', p)" />
    <ScholarsBoard v-else-if="role === 'science'" :game="game" :readonly="readonly" :preview="preview" @act="(p) => $emit('act', p)" />

    <template v-else-if="pickingTech">
      <TechTree :civ="game.civ" :interactive="!readonly" @pick="(id) => choose('research', { tech: id })" />
      <button class="secondary" @click="pickingTech = false">Back</button>
    </template>

    <template v-else-if="pickingDirection">
      <p class="counter-band">Which way?</p>
      <ul class="tech-list">
        <li v-for="d in directions" :key="d.index">
          <button class="tech" :disabled="readonly || d.found" @click="choose('explore', { direction: d.index })">
            <span class="tech__name">{{ d.label }}</span>
            <span class="tech__for">{{ d.found ? 'found' : 'unexplored' }}</span>
            <span class="tech__text">{{ d.found ? d.found : 'Nobody has been that way.' }}</span>
          </button>
        </li>
      </ul>
      <button class="secondary" @click="pickingDirection = false">Back</button>
    </template>

    <template v-else-if="pickingTimes">
      <p class="counter-band">{{ pickingTimes.label }}: how many times? Every spare worker can go the same way.</p>
      <ul class="tech-list">
        <li v-for="n in pickingTimes.max" :key="n">
          <button class="tech" :disabled="readonly" @click="choose(pickingTimes.id, { times: n })">
            <span class="tech__name">×{{ n }}</span>
            <span class="tech__for">costs {{ pickingTimes.costFor(n) }}</span>
            <span class="tech__text">{{ pickingTimes.gainFor(n) }}</span>
          </button>
        </li>
      </ul>
      <button class="secondary" @click="pickingTimes = null">Back</button>
    </template>

    <ul v-else class="action-list" :class="{ 'action-list--preview': preview }">
      <li v-for="a in actions" :key="a.id">
        <button
          class="action"
          :class="{ 'action--off': !a.check.ok, 'action--monument': a.id === 'build-monument', 'action--preview': preview }"
          :disabled="readonly || preview || !a.check.ok"
          @click="tap(a)"
        >
          <span class="action__top">
            <span class="action__label">{{ a.label }}</span>
            <span class="action__gain">{{ a.gain }}</span>
          </span>
          <ActionCost :cost="a.cost">
            <span v-if="a.cap > 1" class="action__cap">up to ×{{ a.cap }}</span>
            <span v-else-if="a.capText" class="action__cap">×1 until more {{ a.capText }}</span>
          </ActionCost>
          <span class="action__blurb">{{ a.blurb }}</span>
          <span v-if="!a.check.ok && a.check.kind !== 'cost'" class="action__why">{{ a.check.why }}</span>
        </button>
      </li>
    </ul>
    <!-- What has happened, under the day's choices rather than over them.
         Matt, 2026-09-19: "The log is taking up too much room and there's
         no reason for it to be at the very top. It needs to move down,
         maybe even below the things to do here." -->
    <Ledger v-if="!preview" :log="game.log" :role="role" />
  </div>
</template>

<script>
import { ROLE_INFO } from '@/engine/constants';
import {
  TECHS, REGIONS, DIRECTIONS, sitesOf,
} from '@/engine/cards';
import {
  actionsForRole, checkAction, resolveCost, resolveLabel, maxTimes, capacity,
} from '@/engine/roleActions';
import { describeCost } from '@/engine/effects';
import TechTree from './TechTree.vue';
import { civFor } from '@/engine/game';
import ActionCost from './ActionCost.vue';
import Ledger from './Ledger.vue';
import FrontierBoard from './FrontierBoard.vue';
import StewardsBoard from './StewardsBoard.vue';
import HallBoard from './HallBoard.vue';
import ScholarsBoard from './ScholarsBoard.vue';

// "+2 food" taken three times is "+6 food"; text with no number is left alone.
export const scaleGain = (gain, n) => gain.replace(/([+-])(\d+)/g, (m, sign, num) => `${sign}${Number(num) * n}`);
// Dice: "2 dice, 1–3 food each (2 to 6)".
export const diceGain = ({ faces, base, resource }, n) => (
  `${n} ${n === 1 ? 'die' : 'dice'}, ${base + 1}–${base + faces} ${resource} each (${n * (base + 1)} to ${n * (base + faces)})`
);

export default {
  name: 'RoleBoard',
  components: {
    TechTree, FrontierBoard, StewardsBoard, HallBoard, ScholarsBoard, ActionCost, Ledger,
  },
  props: {
    game: { type: Object, required: true },
    role: { type: String, required: true },
    readonly: { type: Boolean, default: false },
    // Preview: not this role's turn. Everything is inert, but what you
    // COULD afford stays bright and what you couldn't says what's missing,
    // so you can ask the roles before you for it.
    preview: { type: Boolean, default: false },
    // On your own turn the heading above already names the role; skip the
    // board's own header so a five-action turn fits an iPhone.
    compact: { type: Boolean, default: false },
    // Another sheet (the outcome card) is up: a board's own pop-ups wait.
    hold: { type: Boolean, default: false },
  },
  emits: ['act', 'undo'],
  data() {
    return {
      pickingTech: false, pickingDirection: false, pickingTimes: null, TECHS, ROLE_INFO,
    };
  },
  computed: {
    info() { return ROLE_INFO[this.role]; },

    actions() {
      const civ = civFor(this.game, this.role);
      return actionsForRole(this.role)
        .filter((a) => a.id !== 'build-monument' || civ.monument.revealed)
        .map((a) => ({
          id: a.id,
          label: resolveLabel(a, civ),
          gain: a.gain(civ),
          blurb: a.blurb,
          costText: describeCost(resolveCost(a, civ)),
          cost: resolveCost(a, civ),
          check: checkAction(this.game, this.role, a.id),
          needsParam: a.needsParam,
          max: maxTimes(this.game, this.role, a.id),
          cap: capacity(a, civ),
          capText: a.capText,
          repeat: Boolean(a.repeat),
        }));
    },
    directions() {
      const { civ } = this.game;
      const sites = sitesOf(civ);
      const found = new Set(civ.regions || []);
      return DIRECTIONS.map((name, index) => ({
        index,
        label: name[0].toUpperCase() + name.slice(1),
        found: sites[index] && found.has(sites[index]) ? REGIONS[sites[index]].name : null,
      }));
    },
  },
  watch: {
    role() { this.pickingTech = false; this.pickingDirection = false; this.pickingTimes = null; },
  },
  methods: {
    tap(a) {
      if (a.needsParam === 'tech') { this.pickingTech = true; return; }
      if (a.needsParam === 'direction') { this.pickingDirection = true; return; }
      if (a.repeat && a.max > 1) {
        const action = actionsForRole(this.role).find((x) => x.id === a.id);
        const civ = civFor(this.game, this.role);
        this.pickingTimes = {
          id: a.id,
          label: a.label,
          max: a.max,
          costFor: (n) => describeCost(resolveCost(action, civ, n)),
          gainFor: (n) => (action.dice ? diceGain(action.dice(civ), n) : scaleGain(action.gain(civ), n)),
        };
        return;
      }
      this.choose(a.id);
    },
    choose(actionId, params = {}) {
      this.pickingTech = false;
      this.pickingDirection = false;
      this.pickingTimes = null;
      this.$emit('act', { role: this.role, actionId, params });
    },
  },
};
</script>
